// src/components/Header/AccountMenu.js

import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Logout, Settings } from '@mui/icons-material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import StarIcon from '@mui/icons-material/Star';

import TooltipBasic from '../Controls/Inputs/TooltipBasic';
import { useLogoutMutation, useRetrieveUserQuery } from '../../redux/features/authApi';
import { useGetImageQuery } from '../../redux/features/imageApi';
import { setLogout } from '../../redux/slices/authApiSlice';
import { api } from '../../redux/services/api';

const TypographyStyled = styled(Typography)(() => ({
  '&.MuiTypography-root': {
    fontWeight: 500,
  },
}));

const getInitials = (account) => {
  if (!account) {
    return 'AA';
  }

  const firstInitial = account.first_name?.trim()?.[0] || 'A';

  const lastInitial = account.last_name?.trim()?.[0] || 'A';

  return (firstInitial + lastInitial).toUpperCase();
};

export default function AccountMenu() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const xsMediaOnly = useMediaQuery(theme.breakpoints.only('xs'));

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const { data: account, isLoading: isAccountLoading } = useRetrieveUserQuery();

  const company = account?.company ?? null;

  const avatarImageId = account?.image || null;

  const logoImageId = company?.logo || null;

  const { data: avatarResponse } = useGetImageQuery(avatarImageId ? String(avatarImageId) : '', {
    skip: !avatarImageId,
  });

  const { data: logoResponse } = useGetImageQuery(logoImageId ? String(logoImageId) : '', {
    skip: !logoImageId,
  });

  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  const avatar = avatarResponse?.s3_url || null;

  const logo = logoResponse?.s3_url || null;

  const initials = getInitials(account);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();

    try {
      await logout().unwrap();
    } catch (error) {
      /*
       * The backend session may already be expired. Local authentication
       * state must still be cleared.
       */
      console.error('Logout request failed:', error);
    } finally {
      dispatch(setLogout());

      dispatch(api.util.resetApiState());

      navigate('/', {
        replace: true,
      });

      toast.success(() => t('toast.logged_out_successfully'), {
        style: {
          marginTop: xsMediaOnly ? 0 : 50,
        },
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <TooltipBasic title={t('general.account_settings')} placement="bottom-start">
          <span>
            <IconButton disabled={isAccountLoading} onClick={handleClick} size="small" sx={{ ml: 2 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 35,
                }}
                src={avatar || undefined}
                alt={account ? `${account.first_name || ''} ${account.last_name || ''}`.trim() : ''}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                  }}
                >
                  {initials}
                </Typography>
              </Avatar>
            </IconButton>
          </span>
        </TooltipBasic>
      </Box>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 2,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 2,

            '& .MuiAvatar-root': {
              width: 45,
              height: 45,
              ml: -0.5,
              mr: 1,
              mb: 0,
            },

            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },

            minWidth: 320,
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        <MenuItem component={NavLink} to="/apps/company" onClick={handleClose}>
          <Avatar src={logo || undefined} variant="square">
            {company?.name?.[0] || 'C'}
          </Avatar>

          <TypographyStyled>{t('general.company_profile')}</TypographyStyled>
        </MenuItem>

        <MenuItem component={NavLink} to="/apps/account" onClick={handleClose}>
          <Avatar src={avatar || undefined}>{initials}</Avatar>

          <TypographyStyled>{t('general.account_profile')}</TypographyStyled>
        </MenuItem>

        <MenuItem component={NavLink} to="/apps/subscription" onClick={handleClose}>
          <Avatar
            sx={{
              bgcolor: 'black',
            }}
          >
            <StarIcon />
          </Avatar>

          <TypographyStyled>{t('general.subscription')}</TypographyStyled>
        </MenuItem>

        <Divider />

        <MenuItem component={NavLink} to="/apps/users" onClick={handleClose}>
          <ListItemIcon>
            <PersonAddAltIcon />
          </ListItemIcon>

          <TypographyStyled>{t('general.user_management')}</TypographyStyled>
        </MenuItem>

        <MenuItem component={NavLink} to="/apps/settings" onClick={handleClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>

          <TypographyStyled>{t('settings.settings')}</TypographyStyled>
        </MenuItem>

        <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>

          <TypographyStyled>{t('general.logout')}</TypographyStyled>
        </MenuItem>
      </Menu>
    </>
  );
}
